/**
 * The dashboard's one live connection. Everything that wants server events
 * (the deployment toasts, the server pages, the install guide, the cache's
 * live refresh) subscribes here instead of opening its own socket: one
 * connection is opened while anything listens and closed when nothing does.
 *
 * Events can be missed while the socket is down, so a reconnect is announced
 * as a synthetic `events.reconnected` event; listeners re-read what they show.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LiveEvent = { type: string; projectId?: string; environmentId?: string; serverId?: string; executionId?: string } & Record<string, any>;
type Listener = (event: LiveEvent) => void;

const RECONNECT_AFTER = 3000;

const listeners = new Set<Listener>();
let socket: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let hasConnected = false;

function emit(event: LiveEvent): void {
  for (const listener of [...listeners]) {
    try {
      listener(event);
    } catch (cause) {
      console.error('A live event listener failed', cause);
    }
  }
}

function connect(): void {
  if (socket || listeners.size === 0 || typeof WebSocket === 'undefined') return;
  let ws: WebSocket;
  try {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    ws = new WebSocket(`${protocol}//${window.location.host}/api/v1/events/ws`);
  } catch {
    // No socket here: every listener has its own polling to fall back on.
    return;
  }
  socket = ws;
  ws.onopen = () => {
    if (hasConnected) emit({ type: 'events.reconnected' });
    hasConnected = true;
  };
  ws.onmessage = (message) => {
    try {
      const data = JSON.parse(message.data as string) as LiveEvent;
      if (data && typeof data.type === 'string') emit(data);
    } catch {
      // Not an event (a ping frame, a malformed line): nothing listens for it.
    }
  };
  ws.onclose = () => {
    if (socket === ws) socket = null;
    if (listeners.size > 0 && !reconnectTimer) {
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, RECONNECT_AFTER);
    }
  };
}

/** Listen to live events; returns the way to stop. The socket follows the listeners. */
export function subscribeEvents(listener: Listener): () => void {
  listeners.add(listener);
  connect();
  return () => {
    listeners.delete(listener);
    if (listeners.size > 0) return;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    reconnectTimer = null;
    if (socket) {
      const closing = socket;
      socket = null;
      closing.onclose = null;
      closing.close();
    }
    hasConnected = false;
  };
}
